import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { decorator } from '@instructure/ui-decorator';
import { ownerWindow } from '@instructure/ui-dom-utils';
/**
 * ---
 * category: utilities/react
 * ---
 * A decorator or higher order component that provides methods
 * for cross-origin communication (between iframes/windows).
 *
 * see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
 * @module windowMessageListener
 * @param {Function} messageHandler a handler for messages recieved by the component
 * @param {Function} validSource an optional function that would restrict message handling to a specified source.
 * @returns {Function} a function that decorates a React component with the behavior
 */

var windowMessageListener = decorator(function (ComposedComponent, messageHandler, validSource) {
  var _class, _temp;

  return _temp = _class = /*#__PURE__*/function (_ComposedComponent) {
    _inherits(_class, _ComposedComponent);

    var _super = _createSuper(_class);

    function _class() {
      var _this;

      _classCallCheck(this, _class);

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      _this = _super.call.apply(_super, [this].concat(args));

      _this.handleMessage = function (e) {
        if (_this.sourceIsValid(e.source) && e.origin === origin(_assertThisInitialized(_this)) && e.data) {
          messageHandler.call(_assertThisInitialized(_this), e.data);
        }
      };

      return _this;
    }

    _createClass(_class, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        var win = ownerWindow(this);
        win.addEventListener('message', this.handleMessage, false);

        if (_get(_getPrototypeOf(_class.prototype), "componentDidMount", this)) {
          _get(_getPrototypeOf(_class.prototype), "componentDidMount", this).call(this);
        }
      }
    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        var win = ownerWindow(this);
        win.removeEventListener('message', this.handleMessage, false);

        if (_get(_getPrototypeOf(_class.prototype), "componentDidMount", this)) {
          _get(_getPrototypeOf(_class.prototype), "componentDidMount", this).call(this);
        }
      }
    }, {
      key: "sourceIsValid",
      value: function sourceIsValid(eventSource) {
        var expectedSource = typeof validSource === 'function' ? validSource.call(this) : validSource;

        if (!expectedSource) {
          return true;
        } else if (eventSource) {
          var sourceFrame = eventSource.frameElement;
          var sourceName = sourceFrame ? sourceFrame.getAttribute('name') : null;
          return sourceName === expectedSource;
        } else {
          return false;
        }
      }
    }]);

    return _class;
  }(ComposedComponent), _class.postMessage = function (target, message, origin) {
    target.postMessage(message, origin);
  }, _temp;
});
/**
 * Return the origin of the owner window of the DOM element
 *
 * see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
 *
 * @param {DOMElement} node
 * @returns {String} the origin
 */

function origin(node) {
  var ownWindow = ownerWindow(node);
  var location = ownWindow.location;

  if (location.protocol === 'file:') {
    return '*';
  } else if (location.origin) {
    return location.origin;
  } else if (location.port) {
    return "".concat(location.protocol, "//").concat(location.hostname, ":").concat(location.port);
  } else {
    return "".concat(location.protocol, "//").concat(location.hostname);
  }
}

export default windowMessageListener;
export { origin, windowMessageListener };